import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { useReplaceVaultDocumentMutation } from "#graphql/supergraphHooks";
import {
  type ReplaceVaultDocumentInput,
  type ReplaceVaultDocumentMutation,
  type ReplaceVaultDocumentMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseReplaceVaultDocumentReturn {
  replaceVaultDocument: (
    input: ReplaceVaultDocumentInput,
  ) => Promise<ReplaceVaultDocumentMutation["replaceVaultDocument"] | null>;
  loading: boolean;
  error: ReturnType<typeof useReplaceVaultDocumentMutation>[1]["error"];
}

/**
 * DV-548 — step 1 of a version replace (Mode A, new bytes): mint a new version
 * on an existing document and a presigned upload target for its bytes.
 * Returns `{ document, ssbVersionNumber, uploadTarget }`, or `null` if the
 * mutation returned no data.
 *
 * `changeNote` on this input is ignored whenever new bytes are involved — the
 * backend only persists it here for Mode B (byte-reuse), which this library
 * doesn't drive. Send it to `finalizeVaultDocument` instead. Usually driven
 * via {@link useUploadVaultDocumentVersion}.
 */
export const useReplaceVaultDocument = (
  options?: MutationHookOptions<
    ReplaceVaultDocumentMutation,
    ReplaceVaultDocumentMutationVariables
  >,
): UseReplaceVaultDocumentReturn => {
  const [mutate, { loading, error }] = useReplaceVaultDocumentMutation(options);

  const replaceVaultDocument = useCallback(
    async (input: ReplaceVaultDocumentInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.replaceVaultDocument ?? null;
    },
    [mutate],
  );

  return { replaceVaultDocument, loading, error };
};
